import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useAgentMode } from "@/hooks/useAgentMode";
import { useHasCurrentOrgMembership } from "@/hooks/useHasCurrentOrgMembership";
import { ORGANIZATION_ACCOUNT_TYPE, resolveOrgSideAccount } from "@/lib/orgInvitations";

// Item 15 — reader for the `profile_data.onboardingCompleted` flag that
// OnboardingModal.persistOnboardingData has been writing (PR #169) with no
// reader. Gates the onboarding wizard so it auto-opens exactly once for a new
// agent and the AgentOnboardingBanner nudges agents who skip.
//
// `useCurrentUserProfile` only selects full_name/avatar_url, so this hook runs
// its own `profile_data` query rather than widening that shared hook.
//
// `enabled` (default true) lets a caller that only needs this hook under a
// condition — e.g. the bright-UI Explore fit-scores prompt, which is
// meaningless with the flag off — skip the profile query entirely rather than
// running it on every mount regardless. Disabled, the hook never queries and
// reports `settled: false, needsOnboarding: false`; every existing caller
// passes no argument and keeps today's behaviour unchanged.
export const useOnboardingStatus = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { user, isLoading: authLoading } = useAuth();
  const { isAgent, isAdmin, isLoading: roleLoading } = useUserRole();
  const { isAgentMode } = useAgentMode();

  // The agent wizard is for whoever is acting as an agent right now: a pure
  // agent (role === "agent") OR a dual-role admin who has switched into agent
  // mode. Agent mode is force-false on /admin and /org routes (see
  // useAgentMode), so this can only become true once the admin is genuinely in
  // the agent experience (/explore, /feed, /agent/*) — never in admin context,
  // where the org wizard owns onboarding instead.
  const actingAsAgent = isAgent || (isAdmin && isAgentMode);

  // Org-side accounts have no agent profile to onboard — an org admin's
  // onboarding is the org wizard (useOrgOnboardingStatus), and an invited
  // editor or member-level admin is organization staff, not an agent. Exclude
  // them from the agent wizard entirely, in any mode (resolveOrgSideAccount).
  //
  // Org-side is derived from CURRENT membership (#786), not from a metadata tag
  // that outlives it: a former editor whose membership ended is an agent again
  // and gets the wizard. The membership read is only made when it can change
  // the answer — an "organization" account is org-side on its metadata alone.
  // Note the consequence for dual-role admins: one who currently belongs to an
  // organization is org staff by this rule and is excluded even in agent mode;
  // only an admin with no current membership still gets the agent wizard there.
  const accountType = user?.user_metadata?.account_type;
  const needsMembership =
    enabled && !!user && !roleLoading && actingAsAgent && accountType !== ORGANIZATION_ACCOUNT_TYPE;
  const { hasCurrentMembership, isError: membershipError } = useHasCurrentOrgMembership({
    enabled: needsMembership,
  });
  // A membership read that failed outright fails safe to org-side: a transient
  // error must never nag staff with the agent wizard. A genuine agent gets it on
  // the next clean load — the same trade the profile read below makes.
  const isOrgSideAccount =
    needsMembership && membershipError && hasCurrentMembership === null
      ? true
      : resolveOrgSideAccount(accountType, hasCurrentMembership);
  // Still waiting on the membership read: neither eligible nor settled, so the
  // wizard cannot auto-open for a member before their membership is known.
  const membershipLoading = needsMembership && isOrgSideAccount === null;
  const isOnboardingAgent = actingAsAgent && isOrgSideAccount === false;
  // null = not yet resolved. We never auto-open until this is an explicit false.
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    if (authLoading || roleLoading || membershipLoading) return;

    // Only users acting as an agent have this wizard. Org admins in admin
    // context (and signed-out users) are settled immediately with nothing to do.
    if (!user || !isOnboardingAgent) {
      setOnboardingCompleted(null);
      setProfileLoading(false);
      return;
    }

    let active = true;
    setProfileLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("profile_data")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!active) return;
        if (error) throw error;

        const pd = (data?.profile_data ?? {}) as Record<string, unknown>;
        setOnboardingCompleted(!!pd.onboardingCompleted);
      } catch (err) {
        console.error("Failed to read onboarding status:", err);
        // Fail safe: treat as completed so a transient query error never
        // nags an existing agent with the wizard. A genuinely-new agent will
        // get it on the next clean load.
        if (active) setOnboardingCompleted(true);
      } finally {
        if (active) setProfileLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [enabled, authLoading, roleLoading, membershipLoading, user, isOnboardingAgent]);

  // "all settled" — critical so the wizard never flashes open mid-load.
  // Disabled reports unsettled forever: it never queried, so there is
  // nothing to have settled to, and a caller must not treat it as "no
  // onboarding needed" while it was simply never asked.
  const settled = enabled && !authLoading && !roleLoading && !membershipLoading && !profileLoading;
  const needsOnboarding = settled && !!user && isOnboardingAgent && onboardingCompleted === false;

  return {
    settled,
    isAgent,
    // The hook's own eligibility rule (org-side accounts excluded, dual-role
    // admins included only in agent mode) — distinct from the raw `isAgent`
    // role flag above, which a caller must not use as a substitute gate.
    canOnboard: isOnboardingAgent,
    needsOnboarding,
    // Flip locally the instant the wizard's atomic write lands, so the banner
    // disappears without waiting for a refetch.
    markCompleted: () => setOnboardingCompleted(true),
  };
};
